import { zodResolver } from "@hookform/resolvers/zod";
import { CirclePause, CirclePlay, ImagePlus, Pencil, Save, ShieldCheck, Trash2, UserPlus, Users, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { PageHeader } from "@/components/layout/PageHeader";
import { PartyFields } from "@/components/invoice/PartyFields";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { FieldError, FieldLabel, Input, Select } from "@/components/ui/Field";
import { ErrorPanel, LoadingPanel } from "@/components/ui/Loading";
import { useToast } from "@/components/ui/Toast";
import { currencyOptionLabel } from "@/domain/billing";
import { workspaceSettingsInputSchema } from "@/domain/schemas";
import {
  CURRENCIES,
  type BootstrapPayload,
  type PaymentQrMethod,
  type PlatformWorkspaceSummary,
  type ProvisionWorkspaceInput,
  type UpdatePlatformWorkspaceInput,
  type WorkspaceSettingsInput,
} from "@/domain/types";
import { useAsyncResource } from "@/hooks/useAsyncResource";
import { apiRequest } from "@/lib/api";

async function readImageFile(file: File): Promise<{ dataUrl: string; aspectRatio: number }> {
  if (!file.type.match(/^image\/(png|jpeg|webp)$/)) {
    throw new Error("Upload a PNG, JPEG, or WebP QR image.");
  }
  if (file.size > 1_000_000) {
    throw new Error("Keep each QR image below 1 MB.");
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read the QR image."));
    reader.readAsDataURL(file);
  });

  const aspectRatio = await new Promise<number>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image.naturalWidth / image.naturalHeight || 1);
    image.onerror = () => reject(new Error("Could not inspect the QR image."));
    image.src = dataUrl;
  });

  return { dataUrl, aspectRatio };
}

function settingsFormDefaults(payload: BootstrapPayload): WorkspaceSettingsInput {
  const settings = payload.workspaceSettings;
  return {
    workspaceName: settings.workspace.name,
    company: structuredClone(settings.company),
    defaultCurrency: settings.defaultCurrency,
    invoicePrefix: settings.invoicePrefix,
    invoicePadding: settings.invoicePadding,
    paymentQr: {
      gcash: {
        accountLabel: settings.paymentQrAssets.gcash.accountLabel,
        aspectRatio: settings.paymentQrAssets.gcash.aspectRatio || 1,
      },
      maribank: {
        accountLabel: settings.paymentQrAssets.maribank.accountLabel,
        aspectRatio: settings.paymentQrAssets.maribank.aspectRatio || 1,
      },
    },
  };
}

export function SettingsPage() {
  const resource = useAsyncResource(() => apiRequest<BootstrapPayload>("/api/admin/bootstrap"));
  const { showToast } = useToast();
  const [platformWorkspaces, setPlatformWorkspaces] = useState<PlatformWorkspaceSummary[]>([]);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionName, setProvisionName] = useState("");
  const [provisionEmail, setProvisionEmail] = useState("");
  const [provisionCurrency, setProvisionCurrency] = useState<ProvisionWorkspaceInput["defaultCurrency"]>("USD");
  const [editingWorkspaceId, setEditingWorkspaceId] = useState<string | null>(null);
  const [editWorkspaceName, setEditWorkspaceName] = useState("");
  const [editOwnerEmail, setEditOwnerEmail] = useState("");
  const [workspaceActionId, setWorkspaceActionId] = useState<string | null>(null);
  const [deleteWorkspaceId, setDeleteWorkspaceId] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const form = useForm<WorkspaceSettingsInput>({
    resolver: zodResolver(workspaceSettingsInputSchema),
    mode: "onBlur",
  });

  const loadPlatformWorkspaces = async () => {
    try {
      const rows = await apiRequest<PlatformWorkspaceSummary[]>("/api/admin/platform/workspaces");
      setPlatformWorkspaces(rows);
    } catch {
      setPlatformWorkspaces([]);
    }
  };

  useEffect(() => {
    if (!resource.data) return;
    form.reset(settingsFormDefaults(resource.data));
    if (resource.data.platformOwner) void loadPlatformWorkspaces();
  }, [form, resource.data]);

  const uploadedPreviews = {
    gcash: form.watch("paymentQr.gcash.imageDataUrl"),
    maribank: form.watch("paymentQr.maribank.imageDataUrl"),
  };

  const setQrFile = async (method: PaymentQrMethod, file?: File) => {
    if (!file) return;
    try {
      const image = await readImageFile(file);
      form.setValue(`paymentQr.${method}.imageDataUrl`, image.dataUrl, { shouldDirty: true });
      form.setValue(`paymentQr.${method}.aspectRatio`, image.aspectRatio, { shouldDirty: true });
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Could not load QR image.", "error");
    }
  };

  const removeQr = (method: PaymentQrMethod) => {
    form.setValue(`paymentQr.${method}.imageDataUrl`, null, { shouldDirty: true });
    form.setValue(`paymentQr.${method}.aspectRatio`, 1, { shouldDirty: true });
  };

  const submit = async (values: WorkspaceSettingsInput) => {
    try {
      await apiRequest("/api/admin/settings/workspace", { method: "PUT", body: values });
      showToast("Workspace settings saved.");
      resource.reload();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Could not save workspace settings.", "error");
    }
  };

  const provision = async () => {
    if (!provisionName.trim() || !provisionEmail.trim()) {
      showToast("Enter the friend workspace name and owner email.", "error");
      return;
    }
    setProvisioning(true);
    try {
      const created = await apiRequest<PlatformWorkspaceSummary>("/api/admin/platform/workspaces", {
        method: "POST",
        body: {
          name: provisionName.trim(),
          ownerEmail: provisionEmail.trim().toLowerCase(),
          defaultCurrency: provisionCurrency,
        } satisfies ProvisionWorkspaceInput,
      });
      showToast(`${created.name} private ledger created.`);
      setProvisionName("");
      setProvisionEmail("");
      setProvisionCurrency("USD");
      await loadPlatformWorkspaces();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Could not provision the private ledger.", "error");
    } finally {
      setProvisioning(false);
    }
  };

  const beginWorkspaceEdit = (workspace: PlatformWorkspaceSummary) => {
    if (workspace.protected) return;
    setEditingWorkspaceId(workspace.id);
    setEditWorkspaceName(workspace.name);
    setEditOwnerEmail(workspace.ownerEmail);
    setDeleteWorkspaceId(null);
    setDeleteConfirmText("");
  };

  const cancelWorkspaceEdit = () => {
    setEditingWorkspaceId(null);
    setEditWorkspaceName("");
    setEditOwnerEmail("");
  };

  const saveWorkspaceEdit = async (workspace: PlatformWorkspaceSummary) => {
    const nextName = editWorkspaceName.trim();
    const nextEmail = editOwnerEmail.trim().toLowerCase();
    if (!nextName || !nextEmail) {
      showToast("Enter both the workspace name and owner email.", "error");
      return;
    }

    setWorkspaceActionId(workspace.id);
    try {
      const updated = await apiRequest<PlatformWorkspaceSummary>(
        `/api/admin/platform/workspaces/${workspace.id}`,
        {
          method: "PUT",
          body: {
            name: nextName,
            ownerEmail: nextEmail,
          } satisfies UpdatePlatformWorkspaceInput,
        },
      );
      showToast(
        updated.ownerEmail !== workspace.ownerEmail
          ? "Workspace updated. Also update the allowed email in Cloudflare Access."
          : "Workspace updated.",
      );
      cancelWorkspaceEdit();
      await loadPlatformWorkspaces();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Could not update the private ledger.", "error");
    } finally {
      setWorkspaceActionId(null);
    }
  };

  const setWorkspaceActive = async (workspace: PlatformWorkspaceSummary, active: boolean) => {
    if (workspace.protected) return;
    setWorkspaceActionId(workspace.id);
    try {
      const updated = await apiRequest<PlatformWorkspaceSummary>(
        `/api/admin/platform/workspaces/${workspace.id}`,
        {
          method: "PATCH",
          body: { active },
        },
      );
      showToast(
        updated.active
          ? `${updated.name} reactivated.`
          : `${updated.name} deactivated. Ledgerly access and public invoice links are paused.`,
      );
      if (editingWorkspaceId === workspace.id) cancelWorkspaceEdit();
      if (deleteWorkspaceId === workspace.id) {
        setDeleteWorkspaceId(null);
        setDeleteConfirmText("");
      }
      await loadPlatformWorkspaces();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Could not change workspace status.", "error");
    } finally {
      setWorkspaceActionId(null);
    }
  };

  const beginPermanentDelete = (workspace: PlatformWorkspaceSummary) => {
    if (workspace.protected || workspace.active) return;
    setDeleteWorkspaceId(workspace.id);
    setDeleteConfirmText("");
    setEditingWorkspaceId(null);
  };

  const cancelPermanentDelete = () => {
    setDeleteWorkspaceId(null);
    setDeleteConfirmText("");
  };

  const permanentlyDeleteWorkspace = async (workspace: PlatformWorkspaceSummary) => {
    if (deleteConfirmText !== workspace.name) {
      showToast("Type the workspace name exactly to confirm permanent deletion.", "error");
      return;
    }

    setWorkspaceActionId(workspace.id);
    try {
      await apiRequest<{ deleted: true; workspaceId: string }>(
        `/api/admin/platform/workspaces/${workspace.id}`,
        { method: "DELETE" },
      );
      showToast(`${workspace.name} was permanently deleted.`);
      cancelPermanentDelete();
      await loadPlatformWorkspaces();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Could not permanently delete the private ledger.", "error");
    } finally {
      setWorkspaceActionId(null);
    }
  };

  if (resource.loading) return <LoadingPanel label="Loading workspace settings…" />;
  if (resource.error || !resource.data) return <ErrorPanel error={resource.error ?? new Error("Workspace settings not found.")} onRetry={resource.reload} />;

  const payload = resource.data;
  const currentAssets = payload.workspaceSettings.paymentQrAssets;

  return (
    <>
      <PageHeader
        eyebrow="Private workspace"
        title={payload.workspace.name}
        description={`Signed in as ${payload.adminEmail}. Company details, invoice numbering, clients, invoices, payments, and QR assets are isolated to this workspace.`}
      />

      <form onSubmit={form.handleSubmit(submit)} noValidate className="space-y-6">
        <Card className="p-5 sm:p-6">
          <div className="mb-5">
            <h2 className="text-lg font-black text-ink">Workspace defaults</h2>
            <p className="mt-1 text-sm leading-6 text-ink-muted">These defaults apply only to this private ledger. Existing invoices keep their saved snapshots.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="sm:col-span-2">
              <FieldLabel required htmlFor="workspace-name">Workspace name</FieldLabel>
              <Input id="workspace-name" {...form.register("workspaceName")} />
              <FieldError message={form.formState.errors.workspaceName?.message} />
            </div>
            <div>
              <FieldLabel htmlFor="workspace-currency">Default currency</FieldLabel>
              <Select id="workspace-currency" {...form.register("defaultCurrency")}>
                {CURRENCIES.map((currency) => <option key={currency} value={currency}>{currencyOptionLabel(currency)}</option>)}
              </Select>
            </div>
            <div>
              <FieldLabel required htmlFor="invoice-prefix">Invoice prefix</FieldLabel>
              <Input id="invoice-prefix" className="uppercase" {...form.register("invoicePrefix")} />
              <FieldError message={form.formState.errors.invoicePrefix?.message} />
            </div>
            <div>
              <FieldLabel htmlFor="invoice-padding">Invoice number digits</FieldLabel>
              <Select id="invoice-padding" {...form.register("invoicePadding", { valueAsNumber: true })}>
                {[3, 4, 5, 6].map((value) => <option key={value} value={value}>{value}</option>)}
              </Select>
            </div>
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <div className="mb-5">
            <h2 className="text-lg font-black text-ink">Company profile</h2>
            <p className="mt-1 text-sm leading-6 text-ink-muted">This becomes the issuer information for new invoices in this workspace.</p>
          </div>
          <PartyFields prefix="company" companyMode register={form.register} errors={form.formState.errors} />
        </Card>

        <Card className="p-5 sm:p-6">
          <div className="mb-5">
            <h2 className="text-lg font-black text-ink">Workspace payment QR</h2>
            <p className="mt-1 text-sm leading-6 text-ink-muted">Each private ledger owns its own QR images. QR files are stored in D1 and are served only through authenticated admin requests or a valid client invoice link.</p>
          </div>
          <div className="grid gap-5 lg:grid-cols-2">
            {(["gcash", "maribank"] as const).map((method) => {
              const asset = currentAssets[method];
              const pending = uploadedPreviews[method];
              const removed = pending === null;
              const preview = typeof pending === "string" ? pending : removed ? null : asset.imageUrl;
              return (
                <div key={method} className="rounded-2xl border border-border bg-surface-muted/40 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-black text-ink">{asset.label}</p>
                      <p className="mt-1 text-xs text-ink-muted">{preview ? "QR ready" : "No QR uploaded"}</p>
                    </div>
                    {preview ? (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeQr(method)}>
                        <Trash2 className="h-4 w-4" />Remove
                      </Button>
                    ) : null}
                  </div>

                  <div className="mt-4 flex min-h-48 items-center justify-center rounded-xl border border-dashed border-border bg-white p-3">
                    {preview ? (
                      <img src={preview} alt={`${asset.label} QR preview`} className="max-h-52 max-w-full object-contain" />
                    ) : (
                      <div className="text-center text-sm text-ink-muted">
                        <ImagePlus className="mx-auto mb-2 h-6 w-6" />Upload this workspace&apos;s QR
                      </div>
                    )}
                  </div>

                  <div className="mt-4 space-y-3">
                    <div>
                      <FieldLabel htmlFor={`${method}-account-label`}>Account label shown to clients</FieldLabel>
                      <Input id={`${method}-account-label`} {...form.register(`paymentQr.${method}.accountLabel`)} placeholder="Name · account ending 1234" />
                    </div>
                    <div>
                      <FieldLabel htmlFor={`${method}-qr-file`}>QR image</FieldLabel>
                      <Input
                        id={`${method}-qr-file`}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={(event) => void setQrFile(method, event.target.files?.[0])}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" variant="brand" size="lg" disabled={form.formState.isSubmitting}>
            <Save className="h-5 w-5" />{form.formState.isSubmitting ? "Saving…" : "Save workspace settings"}
          </Button>
        </div>
      </form>

      {payload.platformOwner ? (
        <Card className="mt-8 overflow-hidden">
          <div className="border-b border-border bg-ink px-5 py-5 text-white sm:px-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-brand-300"><ShieldCheck className="h-5 w-5" /></div>
              <div>
                <h2 className="font-black">Provision another private ledger</h2>
                <p className="mt-1 text-sm text-white/65">Creates an isolated workspace for another Access-authenticated email without adding you as a member.</p>
              </div>
            </div>
          </div>
          <div className="grid gap-6 p-5 sm:p-6 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)]">
            <div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <FieldLabel required htmlFor="provision-name">Workspace name</FieldLabel>
                  <Input id="provision-name" value={provisionName} onChange={(event) => setProvisionName(event.target.value)} placeholder="Friend's Ledger" />
                </div>
                <div>
                  <FieldLabel required htmlFor="provision-email">Owner email</FieldLabel>
                  <Input id="provision-email" type="email" value={provisionEmail} onChange={(event) => setProvisionEmail(event.target.value)} placeholder="friend@example.com" />
                </div>
                <div>
                  <FieldLabel htmlFor="provision-currency">Default currency</FieldLabel>
                  <Select id="provision-currency" value={provisionCurrency} onChange={(event) => setProvisionCurrency(event.target.value as ProvisionWorkspaceInput["defaultCurrency"])}>
                    {CURRENCIES.map((currency) => <option key={currency} value={currency}>{currencyOptionLabel(currency)}</option>)}
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button type="button" variant="dark" className="w-full" disabled={provisioning} onClick={() => void provision()}>
                    <UserPlus className="h-4 w-4" />{provisioning ? "Creating…" : "Create private ledger"}
                  </Button>
                </div>
              </div>
              <div className="mt-4 rounded-xl border border-gold-100 bg-gold-50 p-4 text-xs leading-5 text-gold-800">
                After provisioning, make sure that email is also allowed by the Cloudflare Access policy protecting <strong>/app</strong> and <strong>/api/admin</strong>. Ledgerly will then map that identity only to the newly created workspace.
              </div>
            </div>

            <div>
              <div className="mb-3 flex items-center gap-2 text-sm font-black text-ink"><Users className="h-4 w-4" />Provisioned ledgers</div>
              <div className="max-h-[34rem] space-y-3 overflow-auto pr-1">
                {platformWorkspaces.map((workspace) => {
                  const editing = editingWorkspaceId === workspace.id;
                  const deleting = deleteWorkspaceId === workspace.id;
                  const busy = workspaceActionId === workspace.id;

                  return (
                    <div key={workspace.id} className="rounded-xl border border-border bg-surface-muted/50 p-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-bold text-ink">{workspace.name}</p>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${
                              workspace.active
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-slate-200 text-slate-700"
                            }`}>
                              {workspace.active ? "Active" : "Deactivated"}
                            </span>
                            {workspace.protected ? (
                              <span className="rounded-full bg-gold-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-gold-800">
                                Protected
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-1 break-all text-xs text-ink-muted">{workspace.ownerEmail || "Owner email unavailable"}</p>
                          <p className="mt-1 text-[11px] text-ink-muted">
                            {workspace.clientCount} client{workspace.clientCount === 1 ? "" : "s"} · {workspace.invoiceCount} invoice{workspace.invoiceCount === 1 ? "" : "s"}
                          </p>
                        </div>

                        {!workspace.protected && !editing && !deleting ? (
                          <div className="flex flex-wrap gap-2">
                            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => beginWorkspaceEdit(workspace)}>
                              <Pencil className="h-3.5 w-3.5" />Edit
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={workspace.active ? "secondary" : "dark"}
                              disabled={busy}
                              onClick={() => void setWorkspaceActive(workspace, !workspace.active)}
                            >
                              {workspace.active ? <CirclePause className="h-3.5 w-3.5" /> : <CirclePlay className="h-3.5 w-3.5" />}
                              {workspace.active ? "Deactivate" : "Reactivate"}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="danger"
                              disabled={busy || workspace.active}
                              title={workspace.active ? "Deactivate the workspace before deleting it permanently." : undefined}
                              onClick={() => beginPermanentDelete(workspace)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />Delete permanently
                            </Button>
                          </div>
                        ) : null}
                      </div>

                      {workspace.protected ? (
                        <p className="mt-3 rounded-lg border border-gold-100 bg-gold-50 px-3 py-2 text-xs leading-5 text-gold-800">
                          Mark Ledger is protected from platform-level deactivation, owner-email replacement, and permanent deletion.
                        </p>
                      ) : null}

                      {editing ? (
                        <div className="mt-4 space-y-3 rounded-xl border border-border bg-white p-3">
                          <div>
                            <FieldLabel required htmlFor={`workspace-edit-name-${workspace.id}`}>Workspace name</FieldLabel>
                            <Input
                              id={`workspace-edit-name-${workspace.id}`}
                              value={editWorkspaceName}
                              onChange={(event) => setEditWorkspaceName(event.target.value)}
                            />
                          </div>
                          <div>
                            <FieldLabel required htmlFor={`workspace-edit-email-${workspace.id}`}>Owner login email</FieldLabel>
                            <Input
                              id={`workspace-edit-email-${workspace.id}`}
                              type="email"
                              value={editOwnerEmail}
                              onChange={(event) => setEditOwnerEmail(event.target.value)}
                            />
                            <p className="mt-1 text-[11px] leading-4 text-ink-muted">
                              If this changes, update the exact email allowed in the Cloudflare Access policy too.
                            </p>
                          </div>
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={cancelWorkspaceEdit}>
                              <X className="h-3.5 w-3.5" />Cancel
                            </Button>
                            <Button type="button" size="sm" variant="dark" disabled={busy} onClick={() => void saveWorkspaceEdit(workspace)}>
                              <Save className="h-3.5 w-3.5" />{busy ? "Saving…" : "Save changes"}
                            </Button>
                          </div>
                        </div>
                      ) : null}

                      {deleting ? (
                        <div className="mt-4 rounded-xl border border-danger-200 bg-danger-50 p-3">
                          <p className="text-sm font-black text-danger-800">Permanent deletion cannot be undone.</p>
                          <p className="mt-1 text-xs leading-5 text-danger-800">
                            This deletes this workspace&apos;s clients, invoices, invoice items, payments, settings, payment QR assets, membership, and invoice sequence. Public invoice links stop working permanently.
                          </p>
                          <div className="mt-3">
                            <FieldLabel htmlFor={`workspace-delete-confirm-${workspace.id}`}>
                              Type <strong>{workspace.name}</strong> exactly to confirm
                            </FieldLabel>
                            <Input
                              id={`workspace-delete-confirm-${workspace.id}`}
                              value={deleteConfirmText}
                              onChange={(event) => setDeleteConfirmText(event.target.value)}
                              autoComplete="off"
                            />
                          </div>
                          <p className="mt-2 text-[11px] leading-4 text-danger-800">
                            Cloudflare Access is managed separately. Remove the deleted owner email from the Access allowlist when it is no longer needed.
                          </p>
                          <div className="mt-3 flex flex-wrap justify-end gap-2">
                            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={cancelPermanentDelete}>
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="danger"
                              disabled={busy || deleteConfirmText !== workspace.name}
                              onClick={() => void permanentlyDeleteWorkspace(workspace)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />{busy ? "Deleting…" : "Delete forever"}
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-[11px] leading-5 text-ink-muted">
                Deactivation is reversible and pauses both admin access and public invoice links. Permanent deletion is available only after deactivation.
              </p>
            </div>
          </div>
        </Card>
      ) : null}
    </>
  );
}
